import React from 'react';
import { Modal, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CommonModalTitle from './CommonModalTitle';

// ============================================
// CommonModalLayout
// 공용 모달 레이아웃 — 모달 껍데기(중앙 컨테이너 + 헤더 + 콘텐츠 + 푸터)를 표준화.
//
// 프로젝트의 모달들이 같은 패턴(헤더 / 스크롤 콘텐츠 / 푸터)을 매번 직접 짜고 있어서
// 일관성/생산성 확보를 위해 도입. 기존 CommonModalTitle / CommonModalButton과
// 함께 사용한다 (제목 텍스트 / 푸터 버튼은 그대로 둘이 담당).
//
// 사용 예:
//   <CommonModalLayout
//     open={open}
//     onClose={onClose}
//     title="회원 정보 수정"
//     description="(*) 표시는 필수 입력 항목입니다"
//     footer={
//       <>
//         <CommonModalButton variant="cancel" onClick={onClose}>취소</CommonModalButton>
//         <CommonModalButton variant="submit" onClick={onSave}>저장</CommonModalButton>
//       </>
//     }
//   >
//     {children}
//   </CommonModalLayout>
// ============================================

const Container = styled('div', {
  shouldForwardProp: prop => prop !== 'widthValue',
})(({ widthValue }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: typeof widthValue === 'number' ? `${widthValue}px` : widthValue,
  maxWidth: 'calc(100vw - 32px)',
  maxHeight: '90vh',
  backgroundColor: '#fff',
  borderRadius: '12px',
  boxShadow:
    '0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  outline: 'none',
}));

const Header = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '20px 28px',
  background: '#262626',
  flexShrink: 0,
});

const HeaderTopRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '12px',
});

// 라이트 헤더 위에 올라가는 진한 제목 — CommonModalTitle의 'light' variant 재사용.

const HeaderDescription = styled(Typography)({
  fontSize: '13px',
  fontWeight: 400,
  color: '#6b7280',
  letterSpacing: '-0.1px',
});

const CloseButton = styled(IconButton)({
  width: '32px',
  height: '32px',
  padding: 0,
  color: '#fff',
});

const Content = styled('div', {
  shouldForwardProp: prop => prop !== 'noPadding',
})(({ noPadding }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: noPadding ? 0 : '24px 28px',
}));

const Footer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '10px',
  padding: '16px 28px',
  borderTop: '1px solid #e9ecef',
  flexShrink: 0,
});

const CommonModalLayout = ({
  open,
  onClose,
  title,
  description,
  width = 560,
  footer,
  showCloseButton = true,
  disableContentPadding = false,
  children,
  // MUI Modal에 그대로 전달할 추가 props (disableEscapeKeyDown 등)
  ...modalProps
}) => (
  <Modal open={open} onClose={onClose} {...modalProps}>
    <Container widthValue={width}>
      <Header>
        <HeaderTopRow>
          <CommonModalTitle variant="default">{title}</CommonModalTitle>
          {showCloseButton && (
            <CloseButton onClick={onClose} aria-label="close">
              <CloseIcon />
            </CloseButton>
          )}
        </HeaderTopRow>
        {description && <HeaderDescription>{description}</HeaderDescription>}
      </Header>

      <Content noPadding={disableContentPadding}>{children}</Content>

      {footer && <Footer>{footer}</Footer>}
    </Container>
  </Modal>
);

export default CommonModalLayout;
